<script setup lang="ts">
import * as z from 'zod'
import type { FormSubmitEvent } from '@nuxt/ui'

const props = defineProps<{
  coursetypeSlug: string
  coursetypeTitle?: string
}>()

const schema = z.object({
  firstname: z.string().trim().min(1, 'Vorname erforderlich'),
  lastname: z.string().trim().min(1, 'Nachname erforderlich'),
  email: z.email('Ungültige E-Mail-Adresse')
})

type Schema = z.output<typeof schema>

const state = reactive({
  firstname: '',
  lastname: '',
  email: '',
  website: '' // Honeypot — must remain empty.
})

const submitting = ref(false)
const submitError = ref<string | null>(null)
const success = ref(false)

async function onSubmit(event: FormSubmitEvent<Schema>) {
  submitting.value = true
  submitError.value = null
  try {
    const result = await $fetch<{ ok: boolean, error?: string }>('/api/waitinglist', {
      method: 'POST',
      body: {
        coursetype_slug: props.coursetypeSlug,
        firstname: event.data.firstname,
        lastname: event.data.lastname,
        email: event.data.email,
        website: state.website
      }
    })
    if (result.ok) {
      success.value = true
    } else {
      submitError.value = result.error ?? 'Eintrag fehlgeschlagen.'
    }
  } catch {
    submitError.value = 'Verbindung fehlgeschlagen. Bitte versuche es später erneut.'
  } finally {
    submitting.value = false
  }
}
</script>

<template>
  <UCard>
    <template #header>
      <div class="flex items-start gap-2">
        <UIcon
          name="i-lucide-bell-ring"
          class="text-primary text-xl shrink-0 mt-0.5"
        />
        <div>
          <h3 class="font-semibold leading-tight">
            Auf die Warteliste setzen lassen
          </h3>
          <p class="text-sm text-muted mt-1">
            Aktuell ist kein Termin <template v-if="coursetypeTitle">für <strong>{{ coursetypeTitle }}</strong></template>
            ausgeschrieben. Lass dir per E-Mail Bescheid geben, sobald ein neuer Termin verfügbar ist.
          </p>
        </div>
      </div>
    </template>

    <UAlert
      v-if="success"
      icon="i-lucide-circle-check"
      color="success"
      variant="subtle"
      title="Eintrag bestätigt"
      description="Wir melden uns, sobald ein neuer Kurstermin feststeht. Eine Bestätigung wurde an deine E-Mail-Adresse gesendet."
    />

    <UForm
      v-else
      :schema="schema"
      :state="state"
      class="space-y-4"
      @submit="onSubmit"
    >
      <div class="grid gap-4 sm:grid-cols-2">
        <UFormField
          label="Vorname"
          name="firstname"
          required
        >
          <UInput
            v-model="state.firstname"
            class="w-full"
            autocomplete="given-name"
          />
        </UFormField>
        <UFormField
          label="Nachname"
          name="lastname"
          required
        >
          <UInput
            v-model="state.lastname"
            class="w-full"
            autocomplete="family-name"
          />
        </UFormField>
      </div>

      <UFormField
        label="E-Mail"
        name="email"
        required
      >
        <UInput
          v-model="state.email"
          type="email"
          class="w-full"
          autocomplete="email"
        />
      </UFormField>

      <!-- Honeypot: für menschliche Nutzer unsichtbar, Bots füllen es aus -->
      <div
        aria-hidden="true"
        class="absolute left-[-9999px] top-auto w-px h-px overflow-hidden"
      >
        <label>
          Website (bitte leer lassen)
          <input
            v-model="state.website"
            type="text"
            tabindex="-1"
            autocomplete="off"
          >
        </label>
      </div>

      <UAlert
        v-if="submitError"
        icon="i-lucide-alert-triangle"
        color="error"
        variant="subtle"
        :title="submitError"
        description="Bitte versuche es erneut oder schreibe uns an info@web-professionals.ch."
      />

      <UButton
        type="submit"
        :loading="submitting"
        :disabled="submitting"
        trailing-icon="i-lucide-arrow-right"
        label="Auf Warteliste setzen"
      />
    </UForm>
  </UCard>
</template>
